// KPIDashboard.tsx
import React, { useEffect, useState } from 'react';
import { CubeProvider } from '@cubejs-client/react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Modal,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    Button,
} from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import {
    adminGetMobileMetrics,
    adminGetShopMetrics,
    adminGetUploadMetrics,
    adminGetUploadTrend,
    adminGetEamMetrics,
    MobileMetricsResponse,
    ShopMetrics,
    UploadMetrics,
    UploadTrendDataPoint,
    UploadTrendResponse,
    UploadUserDetail,
    EamMetricsResponse,
    AppMetrics,
} from '../admin.kpi.api';

import { cubejsApiForSegment } from 'app.functions';
import AppMetricsCard from '../components/AppMetricsCard';
import AthleteAppVersionChart from '../components/AthleteAppVersionChart';
import EamMetricsCard from '../components/EamMetricsCard';
import AthleteAppLogo from '../../../../../images/fiyrpod-lime.png';
import CoachAppLogo from '../../../../../images/fiyrpod-orange.png';
import { CloseIcon } from 'stream-chat-react';
import PersonIcon from '@mui/icons-material/Person';

// Keep existing AppCard for Coach App (still using Apple API)
interface AppCardProps {
    title: string;
    platform: 'ios' | 'android';
    metrics: AppMetrics | undefined;
    loading: boolean;
}

const AppCard: React.FC<AppCardProps> = ({ title, platform, metrics, loading }) => {
    if (loading) {
        return (
            <Card
                sx={{
                    height: '100%',
                    minHeight: 250,
                    borderRadius: '8px',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #4a3f35',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: '#3d3630',
                        padding: 2,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {platform === 'ios' ? (
                        <AppleIcon sx={{ mr: 1, color: '#fff' }} />
                    ) : (
                        <AndroidIcon sx={{ mr: 1, color: '#FFD700' }} />
                    )}
                    <Typography variant="h6" sx={{ color: '#fff' }}>
                        {title}
                    </Typography>
                </Box>
                <CardContent>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={150}>
                        <CircularProgress sx={{ color: '#a08060' }} />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (!metrics) {
        return (
            <Card
                sx={{
                    height: '100%',
                    minHeight: 250,
                    borderRadius: '8px',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #4a3f35',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: '#3d3630',
                        padding: 2,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {platform === 'ios' ? (
                        <AppleIcon sx={{ mr: 1, color: '#fff' }} />
                    ) : (
                        <AndroidIcon sx={{ mr: 1, color: '#FFD700' }} />
                    )}
                    <Typography variant="h6" sx={{ color: '#fff' }}>
                        {title}
                    </Typography>
                </Box>
                <CardContent>
                    <Typography sx={{ color: '#ff6b6b' }}>No data available</Typography>
                </CardContent>
            </Card>
        );
    }

    const sortedVersions = Object.entries(metrics.versions).sort((a, b) => b[1] - a[1]);

    return (
        <Card
            sx={{
                height: '100%',
                minHeight: 250,
                borderRadius: '8px',
                backgroundColor: '#1a1a1a',
                border: '1px solid #4a3f35',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    backgroundColor: '#3d3630',
                    padding: 2,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {platform === 'ios' ? (
                    <AppleIcon sx={{ mr: 1, fontSize: 28, color: '#fff' }} />
                ) : (
                    <AndroidIcon sx={{ mr: 1, fontSize: 28, color: '#FFD700' }} />
                )}
                <Typography variant="h6" sx={{ color: '#fff' }}>
                    {title}
                </Typography>
            </Box>

            <CardContent>
                {metrics.error && (
                    <Typography sx={{ mb: 2, color: '#ff6b6b' }} variant="body2">
                        Error: {metrics.error}
                    </Typography>
                )}

                {metrics.note && (
                    <Typography sx={{ mb: 2, color: '#a0a0a0' }} variant="body2">
                        {metrics.note}
                    </Typography>
                )}

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                        Total Users
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#a08060' }}>
                        {metrics.total_users.toLocaleString()}
                    </Typography>
                </Box>

                {sortedVersions.length > 0 && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1, color: '#a0a0a0' }}>
                            By Version
                        </Typography>
                        <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                            {sortedVersions.map(([version, count]) => (
                                <Box
                                    key={version}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ py: 0.5 }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'monospace', color: '#d0d0d0' }}
                                    >
                                        v{version}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 'bold', color: '#fff' }}
                                    >
                                        {count.toLocaleString()}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

interface ShopMetricsCardProps {
    metrics: ShopMetrics | null;
    loading: boolean;
}

const ShopMetricsCard: React.FC<ShopMetricsCardProps> = ({ metrics, loading }) => {
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress sx={{ color: '#a08060' }} />
            </Box>
        );
    }

    if (!metrics) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={300}
                sx={{ color: '#6b5d4f' }}
            >
                <Typography variant="h6">No Data Available</Typography>
            </Box>
        );
    }

    const MetricTile: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
        <Box
            sx={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #4a3f35',
                borderRadius: '8px',
                padding: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 140,
                m: 2,
            }}
        >
            <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 1, textAlign: 'center' }}>
                {label}
            </Typography>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 'bold',
                    color: '#a08060',
                    textAlign: 'center',
                }}
            >
                {value}
            </Typography>
        </Box>
    );

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <MetricTile label="Sessions" value={metrics.sessions.toLocaleString()} />
                </Grid>
                <Grid item xs={6}>
                    <MetricTile
                        label="Completed"
                        value={metrics.completedCheckouts.toLocaleString()}
                    />
                </Grid>
                <Grid item xs={6}>
                    <MetricTile label="Drop-off Rate" value={`${metrics.dropOffRate}%`} />
                </Grid>
                <Grid item xs={6}>
                    <MetricTile
                        label="Avg. Session"
                        value={`${metrics.averageSessionLengthMinutes.toFixed(1)}m`}
                    />
                </Grid>
            </Grid>
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #2a2a2a', textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#6b5d4f' }}>
                    Data from {metrics.startDate} to {metrics.endDate}
                </Typography>
            </Box>
        </Box>
    );
};

interface UploadMetricsCardProps {
    metrics: UploadMetrics | null;
    loading: boolean;
}

const UploadMetricsCard: React.FC<UploadMetricsCardProps> = ({ metrics, loading }) => {
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress sx={{ color: '#a08060' }} />
            </Box>
        );
    }

    if (!metrics) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={300}
                sx={{ color: '#6b5d4f' }}
            >
                <Typography variant="h6">No Data Available</Typography>
            </Box>
        );
    }

    const errorRate = 100 - metrics.allTime.successRate;
    const errorRate7Days = 100 - metrics.last7Days.successRate;

    const PercentageMetric: React.FC<{ label: string; value: number; color?: string }> = ({
        label,
        value,
        color = '#a08060',
    }) => (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#a0a0a0', display: 'block' }}>
                {label}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color }}>
                {value.toFixed(2)}%
            </Typography>
        </Box>
    );

    return (
        <Box>
            <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid #2a2a2a' }}>
                <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, color: '#fff', fontWeight: 'bold', textAlign: 'center' }}
                >
                    All Time: {metrics.allTime.totalUploads.toLocaleString()} Total Uploads
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <PercentageMetric
                            label="Success Rate"
                            value={metrics.allTime.successRate}
                            color="#4caf50"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <PercentageMetric label="Error Rate" value={errorRate} color="#ff6b6b" />
                    </Grid>
                    <Grid item xs={6}>
                        <PercentageMetric
                            label="Accurate Data"
                            value={metrics.allTime.accurateDataPercentage}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <PercentageMetric
                            label="Non-Human Data"
                            value={metrics.allTime.nonHumanDataPercentage}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #2a2a2a' }}>
                <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, color: '#fff', fontWeight: 'bold', textAlign: 'center' }}
                >
                    Last 7 Days: {metrics.last7Days.totalUploads.toLocaleString()} Total Uploads
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <PercentageMetric
                            label="Success Rate"
                            value={metrics.last7Days.successRate}
                            color="#4caf50"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <PercentageMetric
                            label="Error Rate"
                            value={errorRate7Days}
                            color="#ff6b6b"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <PercentageMetric
                            label="Accurate Data"
                            value={metrics.last7Days.accurateDataPercentage}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <PercentageMetric
                            label="Non-Human Data"
                            value={metrics.last7Days.nonHumanDataPercentage}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 2, backgroundColor: '#1a1a1a', p: 2, borderRadius: 1 }}>
                <Typography
                    variant="body2"
                    sx={{
                        mb: 2,
                        color: '#fff',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        display: 'block',
                    }}
                >
                    Last 7 Days - Totals
                </Typography>
                <Box>
                    <Box display="flex" justifyContent="space-between" sx={{ py: 0.75 }}>
                        <Typography variant="body2" sx={{ color: '#d0d0d0' }}>
                            Successful Sessions:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                            {metrics.last7Days.totalSuccessfulUploads.toLocaleString()}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" sx={{ py: 0.75 }}>
                        <Typography variant="body2" sx={{ color: '#d0d0d0' }}>
                            Errors:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6b6b' }}>
                            {metrics.last7Days.totalUploadsWithErrors.toLocaleString()}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" sx={{ py: 0.75 }}>
                        <Typography variant="body2" sx={{ color: '#d0d0d0' }}>
                            Accurate Data:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#a08060' }}>
                            {metrics.last7Days.accurateData.toLocaleString()}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" sx={{ py: 0.75 }}>
                        <Typography variant="body2" sx={{ color: '#d0d0d0' }}>
                            Non-Human Data:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ffa726' }}>
                            {metrics.last7Days.nonHumanData.toLocaleString()}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #2a2a2a', textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#6b5d4f' }}>
                    Data from {metrics.last7Days.startDate} to {metrics.last7Days.endDate}
                </Typography>
            </Box>
        </Box>
    );
};

const SectionCard: React.FC<{ title: string; children?: React.ReactNode }> = ({
    title,
    children,
}) => {
    return (
        <Card
            sx={{
                backgroundColor: '#0a0a0a',
                border: '1px solid #6b5d4f',
                borderRadius: '8px',
                height: '100%',
            }}
        >
            <CardContent>
                <Typography variant="h5" sx={{ mb: 3, color: '#fff', fontWeight: 'bold' }}>
                    {title}
                </Typography>
                {children}
            </CardContent>
        </Card>
    );
};

interface UserDetailsModalProps {
    open: boolean;
    onClose: () => void;
    successfulUsers: UploadUserDetail[];
    unsuccessfulUsers: UploadUserDetail[];
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
    open,
    onClose,
    successfulUsers,
    unsuccessfulUsers,
}) => {
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const currentUsers = tabValue === 0 ? successfulUsers : unsuccessfulUsers;

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="user-details-modal"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #4a3f35',
                    borderRadius: '8px',
                    maxHeight: '80vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '95%',
                    maxWidth: '1500px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderBottom: '1px solid #4a3f35',
                        backgroundColor: '#3d3630',
                    }}
                >
                    <Typography variant="h6" sx={{ color: '#fff' }}>
                        GPS Upload User Details (Last 30 Days)
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: '#a0a0a0' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: '1px solid #4a3f35',
                        '& .MuiTabs-indicator': {
                            backgroundColor: tabValue === 0 ? '#4caf50' : '#ff6b6b',
                        },
                    }}
                >
                    <Tab
                        label={`Successful (${successfulUsers.length})`}
                        sx={{
                            color: tabValue === 0 ? '#4caf50' : '#a0a0a0',
                            '&.Mui-selected': { color: '#4caf50' },
                        }}
                    />
                    <Tab
                        label={`Unsuccessful (${unsuccessfulUsers.length})`}
                        sx={{
                            color: tabValue === 1 ? '#ff6b6b' : '#a0a0a0',
                            '&.Mui-selected': { color: '#ff6b6b' },
                        }}
                    />
                </Tabs>

                <TableContainer
                    component={Paper}
                    sx={{ flex: 1, overflow: 'auto', backgroundColor: '#1a1a1a' }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        backgroundColor: '#2a2a2a',
                                        color: '#a0a0a0',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    User Name
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: '#2a2a2a',
                                        color: '#a0a0a0',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Organization
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: '#2a2a2a',
                                        color: '#a0a0a0',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Date
                                </TableCell>
                                {tabValue === 1 && (
                                    <TableCell
                                        sx={{
                                            backgroundColor: '#2a2a2a',
                                            color: '#a0a0a0',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Error
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentUsers.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={tabValue === 1 ? 4 : 3}
                                        sx={{ color: '#6b5d4f', textAlign: 'center', py: 4 }}
                                    >
                                        No data available
                                    </TableCell>
                                </TableRow>
                            ) : (
                                currentUsers.map((user, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            '&:nth-of-type(odd)': { backgroundColor: '#222' },
                                            '&:hover': { backgroundColor: '#333' },
                                        }}
                                    >
                                        <TableCell sx={{ color: '#d0d0d0' }}>
                                            {user.user_name}
                                        </TableCell>
                                        <TableCell sx={{ color: '#d0d0d0' }}>
                                            {user.org_name}
                                        </TableCell>
                                        <TableCell sx={{ color: '#a0a0a0' }}>
                                            {formatDate(user.created_at)}
                                        </TableCell>
                                        {tabValue === 1 && (
                                            <TableCell
                                                sx={{
                                                    color: '#ff6b6b',
                                                    maxWidth: '200px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                title={user.error || 'No error message'}
                                            >
                                                {user.error || 'Incomplete'}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ p: 2, borderTop: '1px solid #4a3f35', textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#6b5d4f' }}>
                        Showing {currentUsers.length}{' '}
                        {tabValue === 0 ? 'successful' : 'unsuccessful'} uploads
                    </Typography>
                </Box>
            </Box>
        </Modal>
    );
};

const KPIDashboard: React.FC = () => {
    // Keep mobileMetricsData for Coach App (still using Apple API)
    const [mobileMetricsData, setMobileMetricsData] = useState<MobileMetricsResponse | null>(null);
    const [shopMetricsData, setShopMetricsData] = useState<ShopMetrics | null>(null);
    const [uploadMetricsData, setUploadMetricsData] = useState<UploadMetrics | null>(null);
    const [uploadTrendData, setUploadTrendData] = useState<UploadTrendDataPoint[] | null>(null);
    const [successfulUsers, setSuccessfulUsers] = useState<UploadUserDetail[]>([]);
    const [unsuccessfulUsers, setUnsuccessfulUsers] = useState<UploadUserDetail[]>([]);
    const [eamMetricsData, setEamMetricsData] = useState<EamMetricsResponse | null>(null);
    const [mobileLoading, setMobileLoading] = useState(true);
    const [shopLoading, setShopLoading] = useState(true);
    const [uploadLoading, setUploadLoading] = useState(true);
    const [uploadTrendLoading, setUploadTrendLoading] = useState(true);
    const [eamLoading, setEamLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [userModalOpen, setUserModalOpen] = useState(false);

    useEffect(() => {
        // Keep fetching mobile metrics for Coach App only
        const fetchMobileMetrics = async () => {
            try {
                setMobileLoading(true);
                const response = await adminGetMobileMetrics();
                setMobileMetricsData(response.data);
            } catch (err: any) {
                console.error('Failed to fetch mobile metrics:', err);
                setError(err.message || 'Failed to load mobile metrics');
            } finally {
                setMobileLoading(false);
            }
        };

        const fetchShopMetrics = async () => {
            try {
                setShopLoading(true);
                const response = await adminGetShopMetrics();
                setShopMetricsData(response.data);
            } catch (err: any) {
                console.error('Failed to fetch shop metrics:', err);
                setError((prev) =>
                    prev ? `${prev} | Failed to load shop metrics` : 'Failed to load shop metrics',
                );
            } finally {
                setShopLoading(false);
            }
        };

        const fetchUploadMetrics = async () => {
            try {
                setUploadLoading(true);
                const response = await adminGetUploadMetrics();
                setUploadMetricsData(response.data);
            } catch (err: any) {
                console.error('Failed to fetch upload metrics:', err);
                setError((prev) =>
                    prev
                        ? `${prev} | Failed to load upload metrics`
                        : 'Failed to load upload metrics',
                );
            } finally {
                setUploadLoading(false);
            }
        };

        const fetchUploadTrend = async () => {
            try {
                setUploadTrendLoading(true);
                const response = await adminGetUploadTrend();
                const data: UploadTrendResponse = response.data;
                setUploadTrendData(data.trend);
                setSuccessfulUsers(data.successful_users);
                setUnsuccessfulUsers(data.unsuccessful_users);
            } catch (err: any) {
                console.error('Failed to fetch upload trend:', err);
                setError((prev) =>
                    prev ? `${prev} | Failed to load upload trend` : 'Failed to load upload trend',
                );
            } finally {
                setUploadTrendLoading(false);
            }
        };

        const fetchEamMetrics = async () => {
            try {
                setEamLoading(true);
                const response = await adminGetEamMetrics(30);
                setEamMetricsData(response.data);
            } catch (err: any) {
                console.error('Failed to fetch EAM metrics:', err);
                setError((prev) =>
                    prev ? `${prev} | Failed to load EAM metrics` : 'Failed to load EAM metrics',
                );
            } finally {
                setEamLoading(false);
            }
        };

        void fetchMobileMetrics(); // Still needed for Coach App
        void fetchShopMetrics();
        void fetchUploadMetrics();
        void fetchUploadTrend();
        void fetchEamMetrics();
    }, []);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #000000 0%, #3d2f1f 100%)',
                padding: 3,
            }}
        >
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ mb: 1, color: '#fff' }}>
                    KPI Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                    Real-time analytics and performance metrics
                </Typography>
            </Box>

            {error && <Typography sx={{ mb: 2, color: '#ff6b6b' }}>{error}</Typography>}

            <Grid container spacing={3}>
                {/* Left Column - App Performance */}
                <Grid item xs={12} lg={3}>
                    <SectionCard title="App Performance">
                        {/* Athlete App - Using CubeJS */}
                        <Box sx={{ mb: 16 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ mb: 2, color: '#a08060', fontWeight: 'bold' }}
                            >
                                <img
                                    src={AthleteAppLogo}
                                    alt="Athlete App Logo"
                                    style={{
                                        height: '20px',
                                        width: '20px',
                                        verticalAlign: 'middle',
                                    }}
                                />
                                &nbsp;Athlete App{' '}
                                <Typography
                                    component="span"
                                    sx={{ fontSize: '0.7rem', color: '#6b5d4f' }}
                                >
                                    (Segment)
                                </Typography>
                            </Typography>
                            <CubeProvider cubejsApi={cubejsApiForSegment}>
                                <Box sx={{ mb: 2 }}>
                                    <AppMetricsCard
                                        title="iOS"
                                        platform="ios"
                                        cubeName="AthleteAppUsers"
                                        cubejsApi={cubejsApiForSegment}
                                    />
                                </Box>
                                <Box>
                                    <AppMetricsCard
                                        title="Android"
                                        platform="android"
                                        cubeName="AthleteAppUsers"
                                        cubejsApi={cubejsApiForSegment}
                                    />
                                </Box>
                            </CubeProvider>
                        </Box>

                        {/* Coach App - Still using Apple API */}
                        <Box sx={{ mt: 16 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ mb: 2, color: '#a08060', fontWeight: 'bold' }}
                            >
                                <img
                                    src={CoachAppLogo}
                                    alt="Coach App Logo"
                                    style={{
                                        height: '20px',
                                        width: '20px',
                                        verticalAlign: 'middle',
                                    }}
                                />
                                &nbsp;Coach App{' '}
                                <Typography
                                    component="span"
                                    sx={{ fontSize: '0.7rem', color: '#6b5d4f' }}
                                >
                                    (Apple API)
                                </Typography>
                            </Typography>
                            <AppCard
                                title="iOS"
                                platform="ios"
                                metrics={mobileMetricsData?.iosMetrics.coach_app}
                                loading={mobileLoading}
                            />
                        </Box>
                    </SectionCard>
                </Grid>

                {/* Right Side - Container for top row and bottom chart */}
                <Grid item xs={12} lg={9}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ minHeight: '400px' }}>
                                <SectionCard title="GPS Performance">
                                    <UploadMetricsCard
                                        metrics={uploadMetricsData}
                                        loading={uploadLoading}
                                    />
                                </SectionCard>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box sx={{ height: '557px' }}>
                                <SectionCard title="EAM Metrics">
                                    <EamMetricsCard metrics={eamMetricsData} loading={eamLoading} />
                                </SectionCard>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box sx={{ height: '557px' }}>
                                <SectionCard title="Shop Performance">
                                    <ShopMetricsCard
                                        metrics={shopMetricsData}
                                        loading={shopLoading}
                                    />
                                </SectionCard>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ minHeight: '400px' }}>
                                <SectionCard title="30-Day Activity Trend | GPS Sessions Overview">
                                    {uploadTrendLoading ? (
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            minHeight={300}
                                        >
                                            <CircularProgress sx={{ color: '#a08060' }} />
                                        </Box>
                                    ) : uploadTrendData && uploadTrendData.length > 0 ? (
                                        <Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    mb: 2,
                                                }}
                                            >
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<PersonIcon />}
                                                    onClick={() => setUserModalOpen(true)}
                                                    sx={{
                                                        color: '#a08060',
                                                        borderColor: '#a08060',
                                                        '&:hover': {
                                                            borderColor: '#c0a080',
                                                            backgroundColor:
                                                                'rgba(160, 128, 96, 0.1)',
                                                        },
                                                    }}
                                                >
                                                    View User Details (
                                                    {successfulUsers.length +
                                                        unsuccessfulUsers.length}
                                                    )
                                                </Button>
                                            </Box>
                                            <Box sx={{ height: 300 }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={uploadTrendData}>
                                                        <CartesianGrid
                                                            strokeDasharray="3 3"
                                                            stroke="#2a2a2a"
                                                        />
                                                        <XAxis
                                                            dataKey="date"
                                                            stroke="#a0a0a0"
                                                            tick={{ fill: '#a0a0a0', fontSize: 12 }}
                                                            angle={-45}
                                                            textAnchor="end"
                                                            height={80}
                                                        />
                                                        <YAxis
                                                            stroke="#a0a0a0"
                                                            tick={{ fill: '#a0a0a0', fontSize: 12 }}
                                                        />
                                                        <Tooltip
                                                            contentStyle={{
                                                                backgroundColor: '#1a1a1a',
                                                                border: '1px solid #4a3f35',
                                                                borderRadius: '4px',
                                                            }}
                                                            labelStyle={{ color: '#fff' }}
                                                        />
                                                        <Legend
                                                            wrapperStyle={{ color: '#a0a0a0' }}
                                                        />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="successful"
                                                            stroke="#4caf50"
                                                            strokeWidth={2}
                                                            name="Successful Uploads"
                                                            dot={{ fill: '#4caf50', r: 3 }}
                                                            activeDot={{ r: 5 }}
                                                        />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="unsuccessful"
                                                            stroke="#ff6b6b"
                                                            strokeWidth={2}
                                                            name="Unsuccessful Uploads"
                                                            dot={{ fill: '#ff6b6b', r: 3 }}
                                                            activeDot={{ r: 5 }}
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            minHeight={300}
                                            sx={{ color: '#6b5d4f' }}
                                        >
                                            <Typography variant="h6">No Data Available</Typography>
                                        </Box>
                                    )}
                                </SectionCard>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Athlete App Version Chart - Using CubeJS */}
                <Grid item xs={12}>
                    <SectionCard title="Athlete App Daily Unique Users by Version">
                        <CubeProvider cubejsApi={cubejsApiForSegment}>
                            <AthleteAppVersionChart cubejsApi={cubejsApiForSegment} />
                        </CubeProvider>
                    </SectionCard>
                </Grid>
            </Grid>

            <UserDetailsModal
                open={userModalOpen}
                onClose={() => setUserModalOpen(false)}
                successfulUsers={successfulUsers}
                unsuccessfulUsers={unsuccessfulUsers}
            />
        </Box>
    );
};

export default KPIDashboard;
